/**
 * The vault, filed automatically.
 *
 * What this replaces: a folder tree rendered from a hardcoded demo constant.
 * It looked like a document management system and contained six fictional
 * files. Meanwhile every real attachment the platform received — the broker's
 * email, the OM, the rent roll, the T12 — sat as a JSON blob on the ingestion
 * that carried it and appeared in no vault at all.
 *
 * Now the server files every ingested file under its deal (name and number) as
 * it arrives, and this reads that. Which means the two things people actually
 * do here work: open a deal and see everything that ever came in for it, or
 * type a word and find the file wherever it landed.
 *
 * Archived deals (the test-data reset archives every deal) are held back
 * unless "Include archived" is on; one that is shown says why it is archived
 * instead of asking the pipeline for a deal it hides (R-16/R-17). A deal's
 * files come out together with "Download all" (R-15).
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import {
  Archive,
  Folder,
  FolderDown,
  FolderOpen,
  ExternalLink,
  Download,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  List,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Table2,
  X,
} from 'lucide-react'
import clsx from 'clsx'

import { documentsService, type VaultDocument } from '@/services/documents'
import { loansService } from '@/services/loans'
import { vaultService, type ArchivedCount, type VaultFolder as VaultGroup } from './vaultService'

const CATEGORY_LABELS: Record<string, string> = {
  correspondence: 'Correspondence',
  rent_roll: 'Rent roll',
  financials: 'Financials',
  appraisal: 'Appraisal',
  insurance: 'Insurance',
  legal: 'Legal',
  marketing: 'Marketing',
  model: 'Model',
  general: 'Other',
  unclassified: 'Unclassified',
}

const PARTY_FIELDS = [
  { key: 'broker' as const, label: 'Broker' },
  { key: 'sponsor' as const, label: 'Sponsor' },
  { key: 'borrower' as const, label: 'Borrower' },
]

/** Folder facts read from the pipeline's deal record rather than the vault. */
const DEAL_FIELDS = new Set(['Requested amount', 'Location', 'Asset type', 'Owner'])

type FolderView = 'grid' | 'list' | 'details'

const VIEW_OPTIONS: { key: FolderView; label: string; Icon: typeof LayoutGrid }[] = [
  { key: 'grid', label: 'Grid view', Icon: LayoutGrid },
  { key: 'list', label: 'List view', Icon: List },
  { key: 'details', label: 'Details view', Icon: Table2 },
]

const VIEW_STORAGE_KEY = 'cmos.vault.folderView'

/** Per-viewer convenience only; storage can be absent or throw. */
function readStoredView(): FolderView {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY)
    return stored === 'list' || stored === 'details' ? stored : 'grid'
  } catch {
    return 'grid'
  }
}

function storeView(view: FolderView) {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view)
  } catch {
    // Not persisted; the choice still applies for this visit.
  }
}

const folderKeyOf = (group: VaultGroup) => group.loanId ?? '__unfiled__'
const groupSize = (group: VaultGroup) => group.documents.reduce((sum, doc) => sum + (doc.sizeBytes ?? 0), 0)
const lastFiledAt = (group: VaultGroup) => group.documents.map((doc) => doc.createdAt).filter(Boolean).sort().slice(-1)[0]
const partyLine = (group: VaultGroup) =>
  PARTY_FIELDS.map(({ key, label }) => (group[key] ? `${label}: ${group[key]}` : null)).filter(Boolean).join(' · ')

function categoryLabel(key: string): string {
  return CATEGORY_LABELS[key] ?? key
}

function fileIcon(doc: VaultDocument) {
  const name = (doc.filename || '').toLowerCase()
  if (doc.category === 'correspondence' || name.endsWith('.eml') || name.endsWith('.msg')) {
    return <Mail size={11} className="text-sky-300/80" />
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv') || name.endsWith('.xlsm')) {
    return <FileSpreadsheet size={11} className="text-emerald-300/80" />
  }
  return <FileText size={11} className="text-amber-300/80" />
}

function humanSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function whenFiled(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

function httpStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined
}

/** A refusal or an absence: asking again gets the same answer. */
function isClientError(error: unknown): boolean {
  const status = httpStatus(error)
  return status != null && status >= 400 && status < 500
}

/** Plain words for a deal-details failure, and whether Retry can help. */
function dealLoadProblem(error: unknown): { text: string; retry: boolean } {
  const status = httpStatus(error)
  if (status === 404) {
    return { text: 'This deal is no longer in the pipeline, so its pipeline details are not shown. Its documents are below.', retry: false }
  }
  if (status === 403) return { text: 'Pipeline details for this deal are not available to your account.', retry: false }
  return { text: 'Deal details could not be loaded. The documents below are unaffected.', retry: true }
}

function archivedLabel(count: ArchivedCount): string {
  return count.deals > 0 || count.documents === 0
    ? `Include archived (${count.deals})`
    : `Include archived (${count.documents} file${count.documents === 1 ? '' : 's'})`
}

function zipName(group: VaultGroup): string {
  const label = group.dealNumber && group.dealNumber !== group.dealName ? `${group.dealName} (${group.dealNumber})` : group.dealName
  return `${label.replace(/[\\/:*?"<>|]/g, '_')} - documents.zip`
}

/** Hand a fetched file to the browser's own download, under its name. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export default function LiveDocumentVault() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [party, setParty] = useState<{ field: 'broker' | 'sponsor' | 'borrower'; value: string } | null>(null)
  const [folderKey, setFolderKey] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [includeArchived, setIncludeArchived] = useState(false)
  const [view, setView] = useState<FolderView>(readStoredView)
  const chooseView = (next: FolderView) => {
    setView(next)
    storeView(next)
  }
  const selectFolder = (group: VaultGroup) => {
    setFolderKey(folderKeyOf(group))
    setDocumentId(null)
    downloadAll.reset()
  }

  const query = useMemo(
    () => ({
      q: search.trim() || undefined,
      ...(party ? { [party.field]: party.value } : {}),
    }),
    [search, party],
  )

  const vault = useQuery({
    queryKey: ['documents', 'vault', query, { includeArchived }],
    queryFn: () => vaultService.list(query, includeArchived),
    staleTime: 15_000,
  })

  const refile = useMutation({
    mutationFn: () => documentsService.refile(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['documents'] }),
  })

  const download = useMutation({
    mutationFn: async (doc: VaultDocument) => ({ blob: await documentsService.download(doc.id), filename: doc.filename }),
    onSuccess: ({ blob, filename }) => saveBlob(blob, filename),
  })

  const downloadAll = useMutation({
    mutationFn: (target: VaultGroup) => vaultService.downloadAll(target.loanId!, zipName(target)),
    onSuccess: ({ blob, filename }) => saveBlob(blob, filename),
  })

  const groups = vault.data?.groups ?? []
  const total = vault.data?.total ?? 0
  const archivedCount = vault.data?.archived ?? { deals: 0, documents: 0 }
  const offerArchived = includeArchived || archivedCount.deals > 0 || archivedCount.documents > 0

  const searching = Boolean(search.trim() || party)
  const group = groups.find((entry) => (entry.loanId ?? '__unfiled__') === folderKey)
  const folder = useQuery({
    queryKey: ['documents', 'vault', 'folder', group?.loanId],
    queryFn: () => vaultService.list({ loanId: group!.loanId! }),
    enabled: Boolean(group?.loanId),
  })
  const activeGroup = folder.data?.groups.find((entry) => entry.loanId === group?.loanId) ?? group
  // An archived deal is off every pipeline screen; asking for it only fails.
  const dealArchived = Boolean(group?.archivedAt)
  const deal = useQuery({
    queryKey: ['loans', group?.loanId],
    queryFn: () => loansService.getById(group!.loanId!),
    enabled: Boolean(group?.loanId) && !dealArchived,
    retry: (failures, error) => !isClientError(error) && failures < 2,
  })
  const dealProblem = deal.isError ? dealLoadProblem(deal.error) : null
  const selected = activeGroup?.documents.find((doc) => doc.id === documentId)

  /** The parties present across the vault, for the filter chips. */
  const parties = useMemo(() => {
    const out: Record<string, Set<string>> = { broker: new Set(), sponsor: new Set(), borrower: new Set() }
    for (const group of groups) {
      for (const { key } of PARTY_FIELDS) {
        const value = group[key]
        if (value) out[key].add(value)
      }
    }
    return out
  }, [groups])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-[280px] flex-1 items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1 focus-within:border-amber-400/30">
          <Search size={12} className="flex-shrink-0 text-stone-600" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filename, deal, deal number, broker, sponsor, borrower…"
            aria-label="Search documents"
            className="min-w-0 flex-1 bg-transparent text-[11px] text-stone-300 outline-none placeholder:text-stone-600"
          />
        </label>

        <select
          value={party ? `${party.field}:${party.value}` : ''}
          onChange={(e) => {
            const raw = e.target.value
            if (!raw) return setParty(null)
            const [field, ...rest] = raw.split(':')
            setParty({ field: field as 'broker' | 'sponsor' | 'borrower', value: rest.join(':') })
          }}
          className="rounded-md border border-amber-400/10 bg-black/40 px-2 py-1 text-[11px] text-stone-300 outline-none focus:border-amber-400/30"
          aria-label="Filter by party"
        >
          <option value="">All parties</option>
          {PARTY_FIELDS.map(({ key, label }) => {
            const values = [...parties[key]].sort()
            if (values.length === 0) return null
            return (
              <optgroup key={key} label={label}>
                {values.map((value) => (
                  <option key={`${key}:${value}`} value={`${key}:${value}`}>
                    {value}
                  </option>
                ))}
              </optgroup>
            )
          })}
        </select>

        {searching ? (
          <button
            type="button"
            onClick={() => {
              setSearch('')
              setParty(null)
            }}
            className="flex items-center gap-1 rounded-md border border-amber-400/10 bg-black/40 px-2 py-1 text-[11px] text-stone-400 hover:text-amber-200"
          >
            <X size={11} /> Clear
          </button>
        ) : null}

        {offerArchived ? (
          <button
            type="button"
            aria-pressed={includeArchived}
            onClick={() => setIncludeArchived((on) => !on)}
            title={`${archivedCount.deals} archived deal${archivedCount.deals === 1 ? '' : 's'} and ${archivedCount.documents} document${archivedCount.documents === 1 ? '' : 's'} held back. Archived records stay on file; they are off the pipeline.`}
            className={clsx(
              'flex items-center gap-1 rounded-md border px-2.5 py-1 text-[11px] focus-visible:outline focus-visible:outline-amber-300',
              includeArchived ? 'border-amber-300/40 bg-amber-300/10 text-amber-200' : 'border-amber-400/10 bg-black/40 text-stone-400 hover:text-amber-200',
            )}
          >
            <Archive size={11} aria-hidden="true" /> {archivedLabel(archivedCount)}
          </button>
        ) : null}

        <div role="group" aria-label="Folder view" className="flex items-center gap-0.5 rounded-md border border-amber-400/10 bg-black/40 p-0.5">
          {VIEW_OPTIONS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              aria-label={label}
              aria-pressed={view === key}
              title={label}
              onClick={() => chooseView(key)}
              className={clsx(
                'rounded p-1 transition-colors focus-visible:outline focus-visible:outline-amber-300',
                view === key ? 'bg-amber-300/15 text-amber-200' : 'text-stone-500 hover:text-amber-200',
              )}
            >
              <Icon size={13} aria-hidden="true" />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => refile.mutate()}
          disabled={refile.isPending}
          title="File anything ingested that has no document row yet. Filing runs on its own after every ingestion; this is for a vault that predates it."
          className="flex items-center gap-1 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1 text-[11px] text-stone-300 hover:text-amber-200 disabled:opacity-50"
        >
          <RefreshCw size={11} className={refile.isPending ? 'animate-spin motion-reduce:animate-none' : undefined} />
          {refile.isPending ? 'Filing…' : 'Re-file intake'}
        </button>
      </div>

      {refile.data ? (
        <div className="text-[10px] text-emerald-400">
          Filed {refile.data.filed}, linked {refile.data.linked} to a deal, {refile.data.skipped} already on file.
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto lg:flex-row lg:overflow-hidden">
        {/* Deals and their documents */}
        <div className="min-h-[180px] min-w-0 flex-1 overflow-y-auto rounded-lg border border-amber-400/10 bg-black/20 p-3 scrollbar-thin">
          {vault.isPending ? (
            <div className="flex items-center gap-2 px-4 py-6 text-[11px] text-stone-500">
              <Loader2 size={12} className="motion-safe:animate-spin" /> Reading the vault…
            </div>
          ) : vault.isError ? (
            <div className="px-4 py-6 text-[11px] text-red-300">
              The vault could not be read.{' '}
              <button type="button" onClick={() => vault.refetch()} className="underline hover:text-red-200">
                Retry
              </button>
            </div>
          ) : groups.length === 0 ? (
            <div className="px-4 py-8 text-center text-[11px] italic text-stone-500">
              {!includeArchived && (archivedCount.deals > 0 || archivedCount.documents > 0)
                ? `${searching ? 'Nothing on a live deal matches that.' : 'No live deal has documents filed yet.'} Archived deals are held back; choose Include archived to show them.`
                : searching
                  ? 'Nothing in the vault matches that.'
                  : 'Nothing filed yet. A deal\u2019s emails and attachments are filed here, under the deal, when it is created or an update to it is applied.'}
            </div>
          ) : (
            view === 'details' ? (
              <DealFolderTable groups={groups} folderKey={folderKey} onSelect={selectFolder} />
            ) : view === 'list' ? (
              <ul className="flex flex-col gap-1" aria-label="Deal folders">
                {groups.map((entry) => (
                  <li key={folderKeyOf(entry)}>
                    <DealFolderRow group={entry} selected={folderKeyOf(entry) === folderKey} onSelect={() => selectFolder(entry)} />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] gap-3">
                {groups.map((entry) => <DealFolder key={folderKeyOf(entry)} group={entry}
                  selected={folderKeyOf(entry) === folderKey}
                  onSelect={() => selectFolder(entry)} />)}
              </div>
            )
          )}
        </div>

        {activeGroup ? (
          <aside aria-label="Folder details" className="min-h-0 w-full shrink-0 overflow-y-auto rounded-lg border border-amber-400/15 bg-[#0b0b0b] p-4 scrollbar-thin lg:w-[42%] lg:max-w-[600px]">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex min-w-0 gap-3">
                <FolderOpen size={30} className="shrink-0 text-amber-300" />
                <div className="min-w-0"><div className="text-[9px] uppercase tracking-widest text-amber-300">Deal folder</div>
                  <h2 className="break-words text-sm font-semibold text-stone-100">{activeGroup.dealName}</h2>
                  <div className="font-mono text-[10px] text-stone-400">{activeGroup.dealNumber || 'Unfiled'}</div>
                </div>
              </div>
              <button type="button" aria-label="Close folder details" onClick={() => { setFolderKey(null); setDocumentId(null) }} className="rounded p-1 text-stone-400 hover:text-white"><X size={15} /></button>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 border-y border-white/10 py-3 text-[11px]">
              {[
                ['Stage', deal.data?.stage || activeGroup.stage],
                // Read from the pipeline, which does not serve an archived deal:
                // left off rather than shown as "Not on record", which is untrue.
                ...(dealArchived ? [] : [
                  ['Requested amount', deal.data?.loanAmount == null ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(deal.data.loanAmount)],
                  ['Property', deal.data?.property || activeGroup.dealName],
                  ['Location', deal.data?.location],
                  ['Asset type', deal.data?.type],
                  ['Owner', deal.data?.assignee],
                ]),
                ['Broker', activeGroup.broker], ['Sponsor', activeGroup.sponsor], ['Borrower', activeGroup.borrower],
                ['Documents', String(activeGroup.documents.length)],
                ['Total size', humanSize(groupSize(activeGroup))],
                ['Last filed', whenFiled(lastFiledAt(activeGroup))],
              ].map(([label, value]) => <div key={label}><dt className="text-[9px] uppercase tracking-wider text-stone-500">{label}</dt><dd className="break-words text-stone-200">{value || (deal.isError && DEAL_FIELDS.has(label ?? '') ? 'Not loaded' : 'Not on record')}</dd></div>)}
            </dl>
            {dealArchived ? (
              <p role="status" className="mt-2 rounded border border-stone-500/30 bg-stone-500/10 px-2 py-1.5 text-[10px] text-stone-300">
                <span className="font-medium text-stone-200">{activeGroup.archiveReason || 'Archived'}</span>
                {activeGroup.archivedAt ? ` on ${whenFiled(activeGroup.archivedAt)}` : ''}. The deal is off the pipeline; its documents stay on file here.
              </p>
            ) : dealProblem ? (
              <p className="mt-2 text-[10px] text-amber-200">
                {dealProblem.text}{' '}
                {dealProblem.retry && <button type="button" onClick={() => deal.refetch()} className="underline">Retry</button>}
              </p>
            ) : null}
            {activeGroup.loanId && (
              <div className="my-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                {!dealArchived && <a href={`/pipeline?deal=${encodeURIComponent(activeGroup.loanId)}&tab=documents`} className="inline-flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-200"><ExternalLink size={12} /> Open deal dossier</a>}
                <button type="button" disabled={downloadAll.isPending || activeGroup.documents.length === 0} onClick={() => downloadAll.mutate(activeGroup)}
                  title="Every file filed under this deal, as one ZIP: a folder per email with what it carried, and uploads in their own folder."
                  className="inline-flex items-center gap-1.5 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-50">
                  {downloadAll.isPending ? <Loader2 size={11} className="animate-spin motion-reduce:animate-none" /> : <FolderDown size={11} aria-hidden="true" />}
                  {downloadAll.isPending ? 'Preparing ZIP…' : `Download all (${activeGroup.documents.length})`}
                </button>
              </div>
            )}
            {downloadAll.isError && <p role="alert" className="mb-2 text-[10px] text-red-300">The ZIP could not be prepared. Try again.</p>}
            <h3 className="mb-2 mt-2 text-[10px] uppercase tracking-widest text-amber-300">Documents</h3>
            {folder.isFetching && <div className="text-[10px] text-stone-500">Loading folder contents…</div>}
            {folder.isError && <p className="text-[10px] text-amber-200">Full folder contents could not be loaded. <button onClick={() => folder.refetch()} className="underline">Retry</button></p>}
            <div className="space-y-1">
              {activeGroup.documents.map((doc) => <button key={doc.id} type="button" onClick={() => { setDocumentId(doc.id); download.reset() }} aria-pressed={selected?.id === doc.id}
                className={clsx('flex w-full items-start gap-2 rounded border px-2 py-2 text-left hover:border-amber-300/30', selected?.id === doc.id ? 'border-amber-300/40 bg-amber-300/10' : 'border-white/[0.06]')}>
                <span className="pt-0.5">{fileIcon(doc)}</span><span className="min-w-0 flex-1"><span className="block break-words text-[11px] text-stone-200">{doc.filename}</span><span className="block text-[9px] text-stone-500">{categoryLabel(doc.category)} · {humanSize(doc.sizeBytes)} · {whenFiled(doc.createdAt)}{doc.archived && !activeGroup.archivedAt ? ' · from an archived intake record' : ''}</span></span>
              </button>)}
            </div>
            {selected && <section aria-label="Document details" className="mt-3 rounded border border-amber-300/15 bg-black/30 p-3">
              <h3 className="mb-2 break-words text-[12px] font-medium text-stone-100">{selected.filename}</h3>
              <dl className="space-y-1 text-[10px]">
                {[
                  ['Category', categoryLabel(selected.category)], ['File type', selected.contentType],
                  ['Size', humanSize(selected.sizeBytes)], ['Version', selected.version == null ? null : String(selected.version)],
                  ['Filed', whenFiled(selected.createdAt)], ['Filed by', selected.uploadedBy],
                  ['Document ID', selected.id], ['Intake ID', selected.ingestionId], ['Storage', selected.storageBackend],
                ].map(([label, value]) => <div key={label} className="grid grid-cols-[80px_minmax(0,1fr)] gap-2"><dt className="text-stone-500">{label}</dt><dd className="break-words text-stone-200">{value || 'Not on record'}</dd></div>)}
              </dl>
              {selected.notes && <p className="mt-2 whitespace-pre-wrap break-words text-[11px] text-stone-300">{selected.notes}</p>}
              <button type="button" disabled={download.isPending} onClick={() => download.mutate(selected)}
                className="mt-3 inline-flex items-center gap-1.5 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-400/10 disabled:opacity-50">
                {download.isPending ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                {download.isPending ? 'Downloading…' : 'Download document'}
              </button>
              {download.isError && <p role="alert" className="mt-2 text-[10px] text-red-300">Document download failed. Try again.</p>}
            </section>}
          </aside>
        ) : null}
      </div>

      <div className="text-[10px] text-stone-600">
        {total} document{total === 1 ? '' : 's'} in view · each deal&apos;s emails and attachments are filed here under the
        deal when it is created or an update to it is applied; a record still in Intake is not filed until then
      </div>
    </div>
  )
}

/** Marks an archived deal's folder; the reason is in the tooltip. */
function ArchivedTag({ group }: { group: VaultGroup }) {
  if (!group.archivedAt) return null
  return (
    <span title={group.archiveReason || 'Archived'} className="ml-1.5 inline-block rounded-full border border-stone-500/40 px-1.5 py-px align-middle text-[8px] font-medium uppercase tracking-wider text-stone-400">
      Archived
    </span>
  )
}

function DealFolder({ group, selected, onSelect }: { group: VaultGroup; selected: boolean; onSelect: () => void }) {
  const Icon = selected ? FolderOpen : Folder
  return <button type="button" aria-label={`Open folder ${group.dealName}`} aria-pressed={selected} onClick={onSelect}
    className={clsx('group flex min-h-[150px] flex-col items-start rounded-lg border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-amber-300', selected ? 'border-amber-300/50 bg-amber-300/[0.08]' : 'border-amber-400/10 bg-[#0b0b0b] hover:border-amber-300/30 hover:bg-amber-300/[0.04]', group.archivedAt && !selected && 'opacity-75')}>
    <div className="mb-3 flex w-full items-center justify-between"><Icon size={38} strokeWidth={1.3} className="fill-amber-300/10 text-amber-300" /><span className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] text-stone-400">{group.documents.length} files</span></div>
    <span className="break-words text-[12px] font-semibold text-stone-100">{group.dealName}<ArchivedTag group={group} /></span>
    <span className="mt-0.5 font-mono text-[9px] text-amber-300/80">{group.dealNumber || 'Unfiled'}</span>
    <span className="mt-2 line-clamp-2 text-[10px] text-stone-500">{partyLine(group) || 'No parties on record'}</span>
  </button>
}

function DealFolderRow({ group, selected, onSelect }: { group: VaultGroup; selected: boolean; onSelect: () => void }) {
  const Icon = selected ? FolderOpen : Folder
  return (
    <button type="button" aria-label={`Open folder ${group.dealName}`} aria-pressed={selected} onClick={onSelect}
      className={clsx('grid w-full grid-cols-[16px_minmax(0,1.4fr)_minmax(0,2fr)_56px_64px] items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-amber-300', selected ? 'border-amber-300/50 bg-amber-300/[0.08]' : 'border-transparent hover:border-amber-300/20 hover:bg-amber-300/[0.04]')}>
      <Icon size={16} strokeWidth={1.5} className="fill-amber-300/10 text-amber-300" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block truncate text-[11px] font-semibold text-stone-100">{group.dealName}<ArchivedTag group={group} /></span>
        <span className="block font-mono text-[9px] text-amber-300/80">{group.dealNumber || 'Unfiled'}</span>
      </span>
      <span className="truncate text-[10px] text-stone-500">{partyLine(group) || 'No parties on record'}</span>
      <span className="text-right font-mono text-[10px] text-stone-400">{group.documents.length} files</span>
      <span className="text-right font-mono text-[10px] text-stone-500">{humanSize(groupSize(group))}</span>
    </button>
  )
}

function DealFolderTable({ groups, folderKey, onSelect }: { groups: VaultGroup[]; folderKey: string | null; onSelect: (group: VaultGroup) => void }) {
  const cell = 'max-w-[160px] truncate px-2 py-1.5'
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[720px] border-collapse text-left text-[11px]">
        <thead>
          <tr className="border-b border-white/10 text-[9px] uppercase tracking-wider text-stone-500">
            {['Deal', 'Deal #', 'Stage', 'Broker', 'Sponsor', 'Borrower'].map((label) => <th key={label} scope="col" className="px-2 py-1.5 font-medium">{label}</th>)}
            {['Files', 'Size', 'Last filed'].map((label) => <th key={label} scope="col" className="px-2 py-1.5 text-right font-medium">{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const selected = folderKeyOf(group) === folderKey
            const Icon = selected ? FolderOpen : Folder
            return (
              <tr key={folderKeyOf(group)} onClick={() => onSelect(group)}
                className={clsx('cursor-pointer border-b border-white/[0.04] transition-colors', selected ? 'bg-amber-300/[0.08]' : 'hover:bg-amber-300/[0.04]')}>
                <td className="max-w-[220px] px-2 py-1.5">
                  {/* Click bubbles to the row; the button carries keyboard access and the accessible name. */}
                  <button type="button" aria-label={`Open folder ${group.dealName}`} aria-pressed={selected}
                    className="flex w-full min-w-0 items-center gap-2 text-left focus-visible:outline focus-visible:outline-amber-300">
                    <Icon size={14} strokeWidth={1.5} className="shrink-0 fill-amber-300/10 text-amber-300" aria-hidden="true" />
                    <span className="truncate font-semibold text-stone-100">{group.dealName}</span>
                    <ArchivedTag group={group} />
                  </button>
                </td>
                <td className={clsx(cell, 'font-mono text-[10px] text-amber-300/80')}>{group.dealNumber || 'Unfiled'}</td>
                <td className={clsx(cell, 'text-stone-300')}>{group.stage || '—'}</td>
                {PARTY_FIELDS.map(({ key }) => <td key={key} className={clsx(cell, 'text-stone-400')} title={group[key] ?? undefined}>{group[key] || '—'}</td>)}
                <td className="px-2 py-1.5 text-right font-mono text-[10px] text-stone-300">{group.documents.length}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right font-mono text-[10px] text-stone-400">{humanSize(groupSize(group))}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right text-[10px] text-stone-400">{whenFiled(lastFiledAt(group))}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
